import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AvailableCoverTypeOptionTypes,
  CoverTypePageContent,
  GroupedCoverTypeMagnoliaDataType,
} from "../types/cover-type.type";
import { groupedCoverTypeMagnoliaData } from "../utils/cover-type.utils";

const CoverTypeContext = createContext<{
  magnoliaData: CoverTypePageContent;
  groupedMagnoliaData: GroupedCoverTypeMagnoliaDataType;
  isDrawerOpen: boolean;
  isInfoDrawerOpen: boolean;
  isCoverTypeModalOpen: boolean;
  selectedAnswer: AvailableCoverTypeOptionTypes;
  toggleWrapperRef: React.RefObject<HTMLDivElement | null>;
  setSelectedAnswer: React.Dispatch<
    React.SetStateAction<AvailableCoverTypeOptionTypes>
  >;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsInfoDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCoverTypeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

function CoverTypeProvider({
  children,
  initialValue,
}: Readonly<{
  children: React.ReactNode;
  initialValue: CoverTypePageContent;
}>) {
  const [selectedAnswer, setSelectedAnswer] =
    useState<AvailableCoverTypeOptionTypes>(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState<boolean>(false);
  const [isCoverTypeModalOpen, setIsCoverTypeModalOpen] =
    useState<boolean>(false);

  const toggleWrapperRef = useRef<HTMLDivElement>(null);

  const groupedMagnoliaData = useMemo(() => {
    return groupedCoverTypeMagnoliaData(initialValue);
  }, [initialValue]);

  const contextValue = useMemo(
    () => ({
      magnoliaData: initialValue,
      groupedMagnoliaData,
      isDrawerOpen,
      isInfoDrawerOpen,
      isCoverTypeModalOpen,
      selectedAnswer,
      toggleWrapperRef,
      setSelectedAnswer,
      setIsDrawerOpen,
      setIsInfoDrawerOpen,
      setIsCoverTypeModalOpen,
    }),

    [
      initialValue,
      groupedMagnoliaData,
      isDrawerOpen,
      isInfoDrawerOpen,
      isCoverTypeModalOpen,
      selectedAnswer,
      toggleWrapperRef,
    ]
  );

  return (
    <CoverTypeContext.Provider value={contextValue}>
      {children}
    </CoverTypeContext.Provider>
  );
}
export const useCoverTypeContext = () => {
  const context = useContext(CoverTypeContext);
  if (!context) {
    throw new Error(
      "useCoverTypeContext must be used within a CoverTypeProvider"
    );
  }
  return context;
};

export { CoverTypeContext };
export default CoverTypeProvider;
